import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaEdit,
  FaThLarge,
  FaImage,
  FaLink,
  FaSortAmountDown,
  FaAlignLeft,
  FaSpinner,
} from "react-icons/fa";
import {
  getProductAppById,
  updateProductApp,
} from "./../../services/productsappServices";
import { toast } from "react-toastify";

const initialForm = {
  nom_app: "",
  description_app: "",
  image_url_app: "",
  lien_url_app: "",
  ordre_app: "",
  id: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

const EditProductAppModal = ({ isOpen, onClose, productId, refreshList }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId || !isOpen) return;

      try {
        setLoading(true);

        const product = await getProductAppById(productId);

        setFormData({
          nom_app: product.nom_app || "",
          description_app: product.description_app || "",
          image_url_app: product.image_url_app || "",
          lien_url_app: product.lien_url_app || "",
          ordre_app: product.ordre_app || "",
          id: product.id || "",
        });
      } catch (error) {
        console.error("Erreur lors du chargement de l'application :", error);
        toast.error("Erreur lors du chargement de l'application");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, isOpen]);

  const orderPreview = useMemo(() => {
    const value = Number(formData.ordre_app);
    if (!Number.isFinite(value) || value <= 0) return "—";
    return value;
  }, [formData.ordre_app]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!formData.id) {
      toast.error("Application introuvable.");
      return;
    }

    if (!formData.nom_app.trim() || !formData.ordre_app) {
      toast.error("Veuillez renseigner au moins le nom et l’ordre d’affichage.");
      return;
    }

    try {
      setSaving(true);
      await updateProductApp(formData.id, formData);
      toast.success("Application mise à jour !");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />

            <div className="relative px-5 py-5 text-white sm:px-6 sm:py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaEdit />
                    Modification
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    Modifier l’application
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Mets à jour le nom, le visuel, la description, le lien et
                    l’ordre d’affichage de cette application.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            {loading ? (
              <div className="flex min-h-[420px] items-center justify-center p-8">
                <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                  <FaSpinner className="animate-spin" />
                  Chargement de l’application...
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                  <div className="space-y-6 xl:col-span-8">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Informations principales"
                        subtitle="Nom, description, image, lien et ordre d’affichage."
                      />

                      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field>
                          <Label required>Nom de l’application</Label>
                          <div className="relative">
                            <FaThLarge className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="nom_app"
                              value={formData.nom_app}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>

                        <Field>
                          <Label required>Ordre d’affichage</Label>
                          <div className="relative">
                            <FaSortAmountDown className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="number"
                              min="1"
                              name="ordre_app"
                              value={formData.ordre_app}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>

                        <Field className="md:col-span-2">
                          <Label>Image</Label>
                          <div className="relative">
                            <FaImage className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="image_url_app"
                              value={formData.image_url_app}
                              onChange={handleChange}
                              placeholder="https://..."
                            />
                          </div>
                        </Field>

                        <Field className="md:col-span-2">
                          <Label>Lien de l’application</Label>
                          <div className="relative">
                            <FaLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="lien_url_app"
                              value={formData.lien_url_app}
                              onChange={handleChange}
                              placeholder="https://..."
                            />
                          </div>
                        </Field>
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Description"
                        subtitle="Texte court affiché pour présenter cette application."
                      />

                      <div className="mt-5">
                        <Label>Description</Label>
                        <div className="relative">
                          <FaAlignLeft className="pointer-events-none absolute left-4 top-4 text-slate-400" />
                          <textarea
                            className={`${inputClass} min-h-[170px] resize-y pl-10 leading-6`}
                            name="description_app"
                            value={formData.description_app}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="space-y-6 xl:col-span-4">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Résumé"
                        subtitle="Aperçu rapide avant enregistrement."
                      />

                      <div className="mt-5 space-y-4">
                        <SummaryRow
                          icon={<FaThLarge />}
                          label="Application"
                          value={formData.nom_app || "Non renseignée"}
                        />
                        <SummaryRow
                          icon={<FaSortAmountDown />}
                          label="Ordre"
                          value={orderPreview}
                        />
                        <SummaryRow
                          icon={<FaImage />}
                          label="Image"
                          value={
                            formData.image_url_app
                              ? "Image renseignée"
                              : "Aucune image"
                          }
                        />
                        <SummaryRow
                          icon={<FaLink />}
                          label="Lien"
                          value={
                            formData.lien_url_app
                              ? "Lien renseigné"
                              : "Aucun lien"
                          }
                        />
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Aperçu image"
                        subtitle="Prévisualisation du visuel renseigné."
                      />

                      <div className="mt-5 flex min-h-[180px] items-center justify-center overflow-hidden rounded-[22px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
                        {formData.image_url_app ? (
                          <img
                            src={formData.image_url_app}
                            alt={formData.nom_app || "Application"}
                            className="max-h-[150px] max-w-full object-contain"
                          />
                        ) : (
                          <div className="text-center text-sm text-slate-400">
                            Aucune image renseignée
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Sauvegarder"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>

      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>

      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>

        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default EditProductAppModal;